import React, { useState, useEffect } from 'react';
import MonthlyChart from './VentasCharts/MonthlyChart';
import QuarterlyChart from './VentasCharts/QuarterlyChart';
import ComparisonChart from './VentasCharts/ComparisonChart';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import ErrorBoundary from 'components/common/ErrorBoundary';

import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const SalesDashboard = () => {

    const { token, setToken } = useToken();
    const { showErrorToast } = useToast();

    const currentYear = new Date().getFullYear();
    const [monthlySelectedYear, setMonthlySelectedYear] = useState(currentYear);
    const [quarterlySelectedYear, setQuarterlySelectedYear] = useState(currentYear);
    const [monthlySelectedVendor, setMonthlySelectedVendor] = useState('TODOS');
    const [quarterlySelectedVendor, setQuarterlySelectedVendor] = useState('TODOS');
    const [comparisonSelectedVendor, setComparisonSelectedVendor] = useState('TODOS');
    // Rango para la comparativa anual
    const [comparisonStartYear, setComparisonStartYear] = useState(currentYear - 2);
    const [comparisonEndYear, setComparisonEndYear] = useState(currentYear);


    const [vendedores, setVendedores] = useState([]);
    const [vendedoresData, setVendedoresData] = useState([]);
    const [monthlyData, setMonthlyData] = useState({});
    const [quarterlyData, setQuarterlyData] = useState({});
    const [yearlyData, setYearlyData] = useState({});
    const [monthlyLoading, setMonthlyLoading] = useState(true);
    const [quarterlyLoading, setQuarterlyLoading] = useState(true);
    const [yearlyLoading, setYearlyLoading] = useState(true);

    /**
    * Función helper para obtener el idUsuario basado en el vendedor seleccionado
    */
    const getVendorId = (selectedVendor) => {
        if (selectedVendor === 'TODOS') return null;

        const vendedor = vendedoresData.find(v =>
            v.vNombre === selectedVendor || v.vUsuario === selectedVendor
        );
        return vendedor ? vendedor.idUsuario : null;
    };

    /**
    * Función para obtener lista de vendedores
    */
    const fetchVendedores = async () => {

        try {
            const response = await fetch('/api/Administradores/getVendedores', {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({})
            });

            if (response?.ok) {
                const responseType = await response.json();

                if (responseType.iResponseType === 1) {
                    const parsed = JSON.parse(responseType.detalles);
                    const dataArray = parsed.table2;

                    // Guardar datos completos
                    setVendedoresData(dataArray);

                    // Procesar vendedores para el formato del dropdown
                    const vendedoresList = ['TODOS', ...dataArray.map(v => v.vNombre || v.vUsuario)];
                    setVendedores(vendedoresList);
                } else {
                    showErrorToast(responseType.vMessage);
                }
            } else {
                showErrorToast(response?.status + ' ' + response?.statusText);

                if (response?.status === 401) {
                    setToken(null);
                    location.href = "";
                }
            }
        } catch (error) {
            showErrorToast('Hubo un problema con la petición Fetch: ' + error.message);
            console.error('❌ Error en fetchVendedores:', error);
        }
    };

    /**
    * Función para obtener datos mensuales
    */
    const fetchMonthlyData = async (year, selectedVendor = 'TODOS') => {
        setMonthlyLoading(true);

        const vendorId = getVendorId(selectedVendor);
        const body = {
            anio: year,
            idUsuario: vendorId
        };

        try {
            const response = await fetch('/api/Administradores/obtenerVentasMensuales', {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });

            if (response?.ok) {
                const responseType = await response.json();

                if (responseType.iResponseType === 1) {
                    const parsed = JSON.parse(responseType.detalles);
                    const dataArray = parsed.table2;

                    // Procesar datos para el formato esperado por la gráfica
                    setMonthlyData(prevData => ({
                        ...prevData,
                        [year]: dataArray || []
                    }));
                } else {
                    showErrorToast(responseType.vMessage);
                }
            } else {
                showErrorToast(response?.status + ' ' + response?.statusText);

                if (response?.status === 401) {
                    setToken(null);
                    location.href = "";
                }
            }
        } catch (error) {
            showErrorToast('Hubo un problema con la petición Fetch: ' + error.message);
            console.error('❌ Error en fetchMonthlyData:', error);
        } finally {
            setMonthlyLoading(false);
        }
    };

    /**
    * Función para obtener datos trimestrales
    */
    const fetchQuarterlyData = async (year, selectedVendor = 'TODOS') => {
        setQuarterlyLoading(true);

        const vendorId = getVendorId(selectedVendor);
        const body = {
            anio: year,
            idUsuario: vendorId
        };

        try {
            const response = await fetch('/api/Administradores/obtenerVentasTrimestrales', {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });

            if (response?.ok) {
                const responseType = await response.json();

                if (responseType.iResponseType === 1) {
                    const parsed = JSON.parse(responseType.detalles);
                    const dataArray = parsed.table2;

                    setQuarterlyData(prevData => ({
                        ...prevData,
                        [year]: dataArray || []
                    }));
                } else {
                    showErrorToast(responseType.vMessage);
                }
            } else {
                showErrorToast(response?.status + ' ' + response?.statusText);

                if (response?.status === 401) {
                    setToken(null);
                    location.href = "";
                }
            }
        } catch (error) {
            showErrorToast('Hubo un problema con la petición Fetch: ' + error.message);
            console.error('❌ Error en fetchQuarterlyData:', error);
        } finally {
            setQuarterlyLoading(false);
        }
    };



    /**
     * Obtiene Datos Anualmente
     */
    const fetchYearlyData = async (selectedVendor = 'TODOS', startYear, endYear) => {
        setYearlyLoading(true);

        const vendorId = getVendorId(selectedVendor);
        const body = {
            idUsuario: vendorId,
            startYear: typeof startYear === 'number' ? startYear : comparisonStartYear,
            endYear: typeof endYear === 'number' ? endYear : comparisonEndYear
        };
        try {
            const response = await fetch('/api/Administradores/obtenerVentasAnuales', {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });

            if (response?.ok) {
                const responseType = await response.json();

                if (responseType.iResponseType === 1) {
                    const parsed = JSON.parse(responseType.detalles);
                    const dataArray = parsed.table2;

                    // Procesar datos para el formato esperado por la gráfica
                    setYearlyData(dataArray || []);
                } else {
                    showErrorToast(responseType.vMessage);
                }
            } else {
                showErrorToast(response?.status + ' ' + response?.statusText);

                if (response?.status === 401) {
                    setToken(null);
                    location.href = "";
                }
            }
        } catch (error) {
            showErrorToast('Hubo un problema con la petición Fetch: ' + error.message);
            console.error('❌ Error en fetchYearlyData:', error);
        } finally {
            setYearlyLoading(false);
        }
    };


    /**
     * Carga vendedores
     */
    useEffect(() => {
        fetchVendedores()
    }, []);

    /**
     * Carga datos iniciales cuando los vendedores estén disponibles
     */
    useEffect(() => {
        if (vendedoresData.length > 0) {
            // Cargar datos mensuales iniciales
            fetchMonthlyData(monthlySelectedYear, monthlySelectedVendor);
            fetchMonthlyData(monthlySelectedYear - 1, monthlySelectedVendor);

            // Cargar datos trimestrales iniciales
            fetchQuarterlyData(quarterlySelectedYear, quarterlySelectedVendor);
            fetchQuarterlyData(quarterlySelectedYear - 1, quarterlySelectedVendor);

            // Cargar datos anuales iniciales
            fetchYearlyData(comparisonSelectedVendor, comparisonStartYear, comparisonEndYear);
        }
    }, [vendedoresData]);

    const ChartSkeleton = () => (
        <div
            style={{
                width: "100%",
                borderRadius: 8,
                border: "1px solid #e3e3e3",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                padding: 24,
                marginBottom: 16,
            }}
        >
            {/* Header con controles */}
            <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
                <Skeleton width={80} height={32} />
                <Skeleton width={150} height={32} />
            </div>

            {/* Área de gráfica */}
            <div style={{ height: 300, marginBottom: 16 }}>
                <Skeleton height="100%" />
            </div>

            {/* Leyenda */}
            <div style={{ display: "flex", justifyContent: "center", gap: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Skeleton width={12} height={12} />
                    <Skeleton width={60} height={12} />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Skeleton width={12} height={12} />
                    <Skeleton width={60} height={12} />
                </div>
            </div>
        </div>
    );



    return (
        <div className="w-full h-full">
            {/* Gráfica Mensual */}
            {monthlyLoading ? (
                <ChartSkeleton />
            ) : (
                    <ErrorBoundary>

                        <MonthlyChart
                            selectedYear={monthlySelectedYear}
                            selectedVendor={monthlySelectedVendor}
                            monthlyData={monthlyData}
                            onYearChange={(year) => {
                                setMonthlySelectedYear(year);
                                fetchMonthlyData(year, monthlySelectedVendor);
                                fetchMonthlyData(year - 1, monthlySelectedVendor);
                            }}
                            onVendorChange={(vendor) => {
                                setMonthlySelectedVendor(vendor);
                                fetchMonthlyData(monthlySelectedYear, vendor);
                                fetchMonthlyData(monthlySelectedYear - 1, vendor);
                            }}
                            vendedores={vendedores}
                                />
                    </ErrorBoundary>

            )}

            {/* Gráfica Trimestral */}
            {quarterlyLoading ? (
                <ChartSkeleton />
            ) : (
                    <ErrorBoundary>

                        <QuarterlyChart
                            selectedYear={quarterlySelectedYear}
                            selectedVendor={quarterlySelectedVendor}
                            quarterlyData={quarterlyData}
                            onYearChange={(year) => {
                                setQuarterlySelectedYear(year);
                                fetchQuarterlyData(year, quarterlySelectedVendor);
                                fetchQuarterlyData(year - 1, quarterlySelectedVendor);
                            }}
                            onVendorChange={(vendor) => {
                                setQuarterlySelectedVendor(vendor);
                                fetchQuarterlyData(quarterlySelectedYear, vendor);
                                fetchQuarterlyData(quarterlySelectedYear - 1, vendor);
                            }}
                            vendedores={vendedores}
                        />
                    </ErrorBoundary>

            )}

            {/* Gráfica Comparativa */}
            {yearlyLoading ? (
                <ChartSkeleton />
            ) : (
                    <ErrorBoundary>

                        <ComparisonChart
                            selectedVendor={comparisonSelectedVendor}
                            yearlyData={yearlyData}
                            onVendorChange={(vendor) => {
                                setComparisonSelectedVendor(vendor);
                                fetchYearlyData(vendor, comparisonStartYear, comparisonEndYear);
                            }}
                            vendedores={vendedores}
                            startYear={comparisonStartYear}
                            endYear={comparisonEndYear}
                            onRangeChange={(s, e) => {
                                setComparisonStartYear(s);
                                setComparisonEndYear(e);
                                fetchYearlyData(comparisonSelectedVendor, s, e);
                            }}
                        />
                    </ErrorBoundary>

            )}
        </div>
    );
};

export default SalesDashboard;